import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import {
  PiBellBold,
  PiChartBarBold,
  PiCheckSquareBold,
  PiFolderBold,
  PiGearBold,
  PiHouseBold,
  PiLifebuoyBold,
  PiLightbulbBold,
  PiTagBold,
  PiUsersBold,
} from "react-icons/pi";
import { Link, NavLink, useLocation, useRouteMatch } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import { useMainStore } from "@/contexts/Store";
import { ACCOUNT_ROUTES } from "@/features/accounts/pages";
import { FEATURE_FLAG_ID } from "@/stores/types/feature-flag-types";

import { userColors } from "../constants";
import { MiniTag } from "../Elements";
import { NavigationLink } from "./navigation/NavigationLink";
import WorkspaceNavigation from "./WorkspaceNavigation";

function DashboardMenu() {
  const mainStore = useMainStore();
  const location = useLocation();
  const { activeWorkspace, workspaceID, isIW } = mainStore.context;
  const { user } = mainStore.users;
  const {
    canManageSettings,
    canManageTags,
    canViewReports,
    canManageAccounts,
  } = mainStore.userPermissions;
  const { list: moduleWorkspaces, moduleInfo } = mainStore.moduleWorkspaces;
  const { isCollaborativeOnly } = mainStore.workspaces;
  const { shutdown } = useIntercom();
  const isReportsMatch = !!useRouteMatch("/workspace-reports");

  const checklistFeatureEnabled = mainStore.featureFlags.getIsEnabled(
    FEATURE_FLAG_ID.COM_CHECKLIST,
  );
  const myTasksEnabled = mainStore.featureFlags.getIsEnabled(
    FEATURE_FLAG_ID.APP_MY_TASKS,
  );

  const moduleWorkspacesToRender = useMemo(
    () =>
      moduleWorkspaces.filter(
        (moduleWorkspace) =>
          moduleWorkspace.added &&
          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          moduleInfo[moduleWorkspace.themis_module.identifier],
      ),
    [moduleWorkspaces, moduleInfo],
  );

  const title = useMemo(() => {
    if (!activeWorkspace) {
      return "";
    }

    return activeWorkspace.is_internal ? "Internal" : "Collaborative";
  }, [activeWorkspace]);

  const triggerTitle = useMemo(() => {
    if (!activeWorkspace) {
      return "Internal";
    }

    return activeWorkspace.is_internal
      ? activeWorkspace.name || activeWorkspace.company.name
      : activeWorkspace.name;
  }, [activeWorkspace]);

  const isLinkSettingsActive = () =>
    location.pathname.startsWith("/settings/") &&
    !location.pathname.includes("/user/profile") &&
    !location.pathname.includes("/user/integrations");

  const isLinkTemplatesActive = location.pathname.startsWith(
    "/resources/compliance-templates",
  );

  // Functions
  // @ts-expect-error TS(7006) FIXME: Parameter 'event' implicitly has an 'any' type.
  function handleSignOut(event) {
    event.preventDefault();

    mainStore.webSessions.delete();
    shutdown();
  }

  return (
    <div className="dashboard-menu">
      <div className="workspace-navigation">
        <WorkspaceNavigation />
      </div>
      <div className="dashboard-menu-list">
        <div className="dashboard-name">
          <div
            className="dashboard-name-block"
            data-testid="dashboard-name-block"
          >
            <h3>{triggerTitle}</h3>
            <span>{title}</span>
          </div>
          <div className="workspace-owner-container">
            <div className="workspace-owner">Company Name</div>
            <div>{activeWorkspace?.company?.name}</div>
          </div>
        </div>
        <ul className="settings-links" data-testid="settings-nav">
          <li
            data-testid="home-nav"
            data-tooltip-id="tooltip"
            data-tooltip-place="right"
            data-tooltip-content="Module summaries"
          >
            <NavLink to={`/workspaces/${workspaceID}/home`}>
              <PiHouseBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
              <span>Home</span>
            </NavLink>
          </li>
        </ul>
        <div
          className="dashboard-settings"
          data-tooltip-id="tooltip"
          data-tooltip-content="User settings and integrations"
          data-tooltip-place="right"
        >
          <NavLink to="/user">
            <span
              style={{
                // @ts-expect-error TS(2538) FIXME: Type 'undefined' cannot be used as an index type.
                background: userColors[mainStore.users.user.icon_color_index],
                minWidth: 20,
                minHeight: 20,
              }}
              className="user-initials"
            >
              {user.initials}
            </span>
            <span className="user-full-name">{user.full_name}</span>
          </NavLink>
        </div>
        <ul className="settings-links" data-testid="settings-nav">
          {myTasksEnabled && (
            <li
              data-testid="my-tasks-nav"
              data-tooltip-id="tooltip"
              data-tooltip-content="View tasks assigned to you"
              data-tooltip-place="right"
            >
              <NavLink to="/my-tasks">
                <PiCheckSquareBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>My Tasks</span>
              </NavLink>
            </li>
          )}
          <li
            data-testid="notifications-nav"
            data-tooltip-id="tooltip"
            data-tooltip-content="View your tasks and approvals"
            data-tooltip-place="right"
          >
            <NavLink to="/notifications">
              <PiBellBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
              <span>Notifications</span>
            </NavLink>
          </li>

          {canManageSettings && (
            <li
              data-tooltip-id="tooltip"
              data-tooltip-content="Manage users, modules, and more"
              data-tooltip-place="right"
            >
              <NavLink
                to="/settings"
                data-testid="settings-nav-link"
                isActive={isLinkSettingsActive}
              >
                <PiGearBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>Settings</span>
              </NavLink>
            </li>
          )}
          {!(activeWorkspace && !activeWorkspace.is_internal) &&
            canManageTags && (
              <li
                data-tooltip-id="tooltip"
                data-tooltip-content="Manage organization-wide tags"
                data-tooltip-place="right"
              >
                <NavLink to="/tags">
                  <PiTagBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                  <span>Tags</span>
                </NavLink>
              </li>
            )}

          {canViewReports && (
            <li
              data-tooltip-id="tooltip"
              data-tooltip-content="View and export reports"
              data-tooltip-place="right"
            >
              <NavLink to="/workspace-reports" replace={isReportsMatch}>
                <PiChartBarBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>Reports</span>
              </NavLink>
            </li>
          )}

          {canManageAccounts && (
            <li
              data-tooltip-id="tooltip"
              data-tooltip-content="Manage accounts"
              data-tooltip-place="right"
            >
              <NavLink to={`/workspaces/${workspaceID}${ACCOUNT_ROUTES.base}`}>
                <PiUsersBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>Accounts</span>
              </NavLink>
            </li>
          )}
        </ul>

        <hr />

        <h4>
          <PiFolderBold className="tw-h-4 tw-w-4 tw-stroke-[#21205f]" />
          Modules
        </h4>
        <ul className="dashboard-menu-list-links">
          {moduleWorkspacesToRender.map((moduleWorkspace) => {
            const moduleID = moduleWorkspace.themis_module.identifier;
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            const { url, badge, basePath } = moduleInfo[moduleID];
            const { name } = moduleWorkspace;
            return (
              <NavigationLink
                key={url}
                badge={badge}
                url={url}
                basePath={basePath}
                name={name}
                moduleID={moduleID}
              />
            );
          })}
          {isIW && checklistFeatureEnabled && (
            <NavigationLink
              key="todo-items"
              badge
              url="/todos"
              basePath="/todos"
              name="Checklist"
              moduleID="todos"
            />
          )}
        </ul>

        {isCollaborativeOnly && (
          <>
            <hr />
            <h4>
              <PiLifebuoyBold className="tw-h-4 tw-w-4 tw-stroke-[#21205f]" />
              Resources
            </h4>

            <ul className="dashboard-menu-list-links">
              <li>
                <Link
                  to="/resources/compliance-templates"
                  className={classNames("dashboard-menu-compliance-link", {
                    active: isLinkTemplatesActive,
                  })}
                >
                  Templates
                  <MiniTag theme="cherry" label="New" />
                </Link>
              </li>
            </ul>
          </>
        )}

        <hr />

        <h4>
          <PiLightbulbBold className="tw-h-4 tw-w-4 tw-stroke-[#21205f]" />
          Authentication
        </h4>

        <ul className="dashboard-menu-list-links">
          {mainStore.webSessions.accessToken ? (
            <li>
              <Link
                to="/sign-out"
                onClick={handleSignOut}
                data-testid="sign-out"
              >
                Sign Out
              </Link>
            </li>
          ) : (
            <>
              <li>
                <Link to="/sign-in">Sign In</Link>
              </li>
              <li>
                <Link to="/sign-up">Sign Up</Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}

export default observer(DashboardMenu);
